import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Paper, Grid, Button, CircularProgress, Alert, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useDebouncedValue } from '../../../../utility/useDebouncedValue';
import { useAppDispatch, useAppSelector } from '../../../../store/store';
import { ActorSearchResult } from '../../../../store/model/searchModel';
import { Actor } from '../../../../store/model/actorModel';
import { clearActorMergeSearch, getActorMergeActorDetails, mergeActors, searchActorMergeActors } from '../../../../store/actions/actorMergeActions';
import { actorMergeActorDetailsLoadingSelector, actorMergeActorDetailsSelector, actorMergeSearchLoadingSelector, actorMergeSearchResultsSelector } from '../../../../store/selectors/actorMergeSelectors';

type ActorMergeSettingsPageProps = {
  isNavOpen?: boolean;
  setIsNavOpen?: (open: boolean) => void;
};

export default function ActorMergeSettingsPage({ isNavOpen, setIsNavOpen }: ActorMergeSettingsPageProps) {
  const dispatch = useAppDispatch();

  const [targetActor, setTargetActor] = useState<ActorSearchResult | null>(null);
  const [sourceActor, setSourceActor] = useState<ActorSearchResult | null>(null);
  const [targetInput, setTargetInput] = useState('');
  const [sourceInput, setSourceInput] = useState('');

  const [isMerging, setIsMerging] = useState(false);
  const [mergeError, setMergeError] = useState<string | null>(null);
  const [mergeComplete, setMergeComplete] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Debounced values
  const debouncedTargetInput = useDebouncedValue(targetInput, 400);
  const debouncedSourceInput = useDebouncedValue(sourceInput, 400);

  const targetOptions = useAppSelector(actorMergeSearchResultsSelector('target'));
  const sourceOptions = useAppSelector(actorMergeSearchResultsSelector('source'));
  const targetSearching = useAppSelector(actorMergeSearchLoadingSelector('target'));
  const sourceSearching = useAppSelector(actorMergeSearchLoadingSelector('source'));

  const targetActorId = targetActor?.actorId ?? null;
  const sourceActorId = sourceActor?.actorId ?? null;

  const targetDetails = useAppSelector(actorMergeActorDetailsSelector(targetActorId));
  const sourceDetails = useAppSelector(actorMergeActorDetailsSelector(sourceActorId));
  const targetDetailsLoading = useAppSelector(actorMergeActorDetailsLoadingSelector(targetActorId));
  const sourceDetailsLoading = useAppSelector(actorMergeActorDetailsLoadingSelector(sourceActorId));

  useEffect(() => {
    if (debouncedTargetInput.trim().length === 0) {
      dispatch(clearActorMergeSearch({ key: 'target' }));
      return;
    }

    dispatch(searchActorMergeActors({ key: 'target', searchText: debouncedTargetInput, limit: 5 }));
  }, [debouncedTargetInput, dispatch]);

  useEffect(() => {
    if (debouncedSourceInput.trim().length === 0) {
      dispatch(clearActorMergeSearch({ key: 'source' }));
      return;
    }

    dispatch(searchActorMergeActors({ key: 'source', searchText: debouncedSourceInput, limit: 5 }));
  }, [debouncedSourceInput, dispatch]);

  useEffect(() => {
    if (targetActorId == null) return;
    dispatch(getActorMergeActorDetails({ actorId: targetActorId }));
  }, [dispatch, targetActorId]);

  useEffect(() => {
    if (sourceActorId == null) return;
    dispatch(getActorMergeActorDetails({ actorId: sourceActorId }));
  }, [dispatch, sourceActorId]);

  const optionLabel = useMemo(() => {
    return (option: ActorSearchResult) => `${option.firstName} ${option.lastName}`.trim();
  }, []);

  const selectionError = useMemo(() => {
    if (targetActorId == null || sourceActorId == null) return null;
    if (targetActorId === sourceActorId) return 'Target and source must be different actors.';
    return null;
  }, [sourceActorId, targetActorId]);

  const canMerge = useMemo(() => {
    return !isMerging && targetActorId != null && sourceActorId != null && targetActorId !== sourceActorId;
  }, [isMerging, sourceActorId, targetActorId]);

  const onMerge = async () => {
    if (!canMerge || targetActorId == null || sourceActorId == null) return;

    setIsMerging(true);
    setMergeError(null);
    setMergeComplete(false);

    try {
      await dispatch(mergeActors({ targetActorId, sourceActorId })).unwrap();
      setMergeComplete(true);

      setTargetActor(null);
      setSourceActor(null);
      setTargetInput('');
      setSourceInput('');
      dispatch(clearActorMergeSearch({ key: 'target' }));
      dispatch(clearActorMergeSearch({ key: 'source' }));
    } catch (err) {
      setMergeError(typeof err === 'string' ? err : 'Merge failed.');
    } finally {
      setIsMerging(false);
    }
  };

  const onRequestMerge = () => {
    if (!canMerge) return;
    setConfirmOpen(true);
  };

  const onCancelMerge = () => {
    if (isMerging) return;
    setConfirmOpen(false);
  };

  const onConfirmMerge = async () => {
    if (isMerging) return;
    setConfirmOpen(false);
    await onMerge();
  };

  const renderActorDetails = (searchResult: ActorSearchResult | null, actor: Actor | null, loading: boolean) => (
    actor || searchResult ? (
      <Paper elevation={2} sx={{ p: 2, minWidth: 250, opacity: loading ? 0.7 : 1 }}>
        <Typography variant="h6">
          {actor ? `${actor.firstName} ${actor.lastName}`.trim() : optionLabel(searchResult as ActorSearchResult)}
        </Typography>

        <Typography variant="body2"><b>ID:</b> {actor ? actor.id : (searchResult as ActorSearchResult).actorId}</Typography>
        <Typography variant="body2"><b>Email:</b> {actor ? actor.emailAddress : (searchResult as ActorSearchResult).emailAddress}</Typography>
        <Typography variant="body2"><b>ActTrack Code:</b> {((actor?.actTrakActorCode ?? "-") as string)}</Typography>

        {loading && (
          <Typography variant="caption" color="text.secondary">Loading full details…</Typography>
        )}
      </Paper>
    ) : (
      <Paper elevation={0} sx={{ p: 2, minWidth: 250, color: 'text.disabled' }}>
        <Typography variant="body2">No actor selected</Typography>
      </Paper>
    )
  );

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', mt: 4 }}>
      <Typography variant="h4" gutterBottom>Actor Merge Settings</Typography>
      <Grid container spacing={4} alignItems="center">
        <Grid item xs={5}>
          <Autocomplete
            options={targetOptions}
            getOptionLabel={optionLabel}
            filterOptions={(options) => options}
            value={targetActor}
            onChange={(_, value) => setTargetActor(value)}
            inputValue={targetInput}
            onInputChange={(_, value) => setTargetInput(value)}
            renderInput={params => <TextField {...params} label="To Actor" variant="outlined" />}
            isOptionEqualToValue={(option, value) => option.actorId === value.actorId}
            loading={targetSearching}
            disabled={isMerging}
          />
          <Box mt={2}>{renderActorDetails(targetActor, targetDetails, targetDetailsLoading)}</Box>
        </Grid>
        <Grid item xs={2} sx={{ textAlign: 'center' }}>
          <ArrowBackIcon sx={{ fontSize: 48, color: 'primary.main', mt: 6 }} />
        </Grid>
        <Grid item xs={5}>
          <Autocomplete
            options={sourceOptions}
            getOptionLabel={optionLabel}
            filterOptions={(options) => options}
            value={sourceActor}
            onChange={(_, value) => setSourceActor(value)}
            inputValue={sourceInput}
            onInputChange={(_, value) => setSourceInput(value)}
            renderInput={params => <TextField {...params} label="From Actor" variant="outlined" />}
            isOptionEqualToValue={(option, value) => option.actorId === value.actorId}
            loading={sourceSearching}
            disabled={isMerging}
          />
          <Box mt={2}>{renderActorDetails(sourceActor, sourceDetails, sourceDetailsLoading)}</Box>
        </Grid>
      </Grid>

      <Box mt={4}>
        {selectionError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {selectionError}
          </Alert>
        )}

        {mergeError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {mergeError}
          </Alert>
        )}

        {mergeComplete && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Merge completed.
          </Alert>
        )}

        <Box display="flex" alignItems="center" gap={2}>
          <Button
            variant="contained"
            color="primary"
            disabled={!canMerge}
            onClick={onRequestMerge}
            startIcon={isMerging ? <CircularProgress size={18} color="inherit" /> : undefined}
          >
            {isMerging ? 'Merging…' : 'Merge'}
          </Button>

          <Typography variant="body2" color="text.secondary">
            This can take a few minutes. Please don’t close the tab.
          </Typography>
        </Box>
      </Box>

      <Dialog
        open={confirmOpen}
        onClose={onCancelMerge}
        aria-labelledby="actor-merge-confirm-title"
        aria-describedby="actor-merge-confirm-description"
      >
        <DialogTitle id="actor-merge-confirm-title">Confirm merge</DialogTitle>
        <DialogContent>
          <DialogContentText id="actor-merge-confirm-description">
            This will merge the source actor into the target actor. This change can't be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancelMerge} disabled={isMerging}>Cancel</Button>
          <Button onClick={onConfirmMerge} variant="contained" color="primary" disabled={!canMerge || isMerging}>
            Merge
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
